import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import spacetime from 'spacetime';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import TIMEZONES from 'consts/timezones';
import { InputShape } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';
import AsyncSelect from 'lib/AsyncSelect';
import { SelectDataHandler, SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { MINUTES_IN_HOUR } from 'utils/dateFormatUtil';
import { setTimezone, Timezone, TimezoneWithPublisher } from 'store/header/reducer';
import { getEnvironmentId } from 'store/environment/selectors';
import { getFlag, getIso2CountryCode, getLocaleCode, getTimezone } from 'store/publisher/selectors';
import { Option, SingleValue } from './AsyncSelectCustomComponents';

const formatHours = (hours: number): string => {
  const minutes = `${Math.abs(hours * MINUTES_IN_HOUR) % MINUTES_IN_HOUR}`.padStart(2, '0');
  const absHours = `${Math.abs(hours)}`.padStart(2, '0');
  const time = `${absHours}:${minutes}`;

  if (hours > 0) return `+${time}`;
  if (hours < 0) return `-${time}`;

  return `${time}`;
};

const SelectTimezone: React.FC = () => {
  const dispatch = useDispatch();
  const environmentId = useSelector(getEnvironmentId);
  const tz = useSelector((state: Store) => state.header.timezone);

  const iso2CountryCode = useSelector(getIso2CountryCode);
  const localeCode = useSelector(getLocaleCode);
  const code = useSelector(getTimezone);
  const flagOverride = useSelector(getFlag);

  const timezoneAbr = (timeZone: string): string => {
    return (
      new Intl.DateTimeFormat(localeCode, {
        timeZoneName: 'short',
        timeZone,
      })
        .formatToParts()
        .find(({ type }) => type === 'timeZoneName')?.value ?? ''
    );
  };

  const formattedZones = useMemo(() => {
    const hasTimezone = TIMEZONES.find((config) => config.code === code && config.iso2CountryCode === iso2CountryCode);
    const timezones = (
      !hasTimezone ? [...TIMEZONES, { iso2CountryCode, code, tzAbr: timezoneAbr(code), flagOverride }] : TIMEZONES
    ) as Timezone[];

    return timezones.map((zone) => {
      zone.flag = zone.flagOverride ? `${zone.flagOverride}.png` : `${zone.iso2CountryCode}.png`;
      const now = spacetime.now(zone.code);
      const {
        current: { offset: hours },
      } = now.timezone();
      return { ...zone, name: `UTC ${formatHours(hours)} | ${zone.tzAbr}` };
    });
  }, [TIMEZONES, localeCode]);

  const setDefaultTimezone = (): void => {
    const defaultTimezone = formattedZones.find((zone) => zone.code === code) || formattedZones[0];
    dispatch(setTimezone({ ...defaultTimezone, selectedPublisher: environmentId }));
  };

  const onLoadLookupData: SelectDataHandler<Timezone> = (_, query): Timezone[] => {
    return formattedZones.filter((zone) => zone.name.toLowerCase().indexOf(query.toLowerCase()) > -1);
  };

  const onLookupSelect: SelectHandler<TimezoneWithPublisher> = (_, newValue: TimezoneWithPublisher): void => {
    dispatch(setTimezone({ ...newValue, selectedPublisher: environmentId }));
  };

  useEffect(() => {
    if (!code || !environmentId || (tz.code !== '' && tz.selectedPublisher === environmentId)) return;

    setDefaultTimezone();
  }, [code, tz, environmentId]);

  return (
    <AsyncSelect
      dataTestId="timezone"
      name="timezone"
      loadData={onLoadLookupData}
      selectedValue={tz.code ? tz : null}
      placeholder="Select Time zone"
      onSelect={onLookupSelect}
      isClearable={false}
      defaultOptions={formattedZones}
      debounceTime={0}
      customComponents={{ SingleValue, Option }}
      isDisabled={!Auth.hasPermission(PermissionsEnum.DASHBOARD_TIMEZONE_SELECT)}
      shape={InputShape.CIRCLE}
    />
  );
};

export default SelectTimezone;
